import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useV2GetApprovalLevelQuery,
  useV2ListUsersForAccountQuery,
  useV2ListLineOfBusinessesForAccountQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  AdminFormPage,
  useAdminFormPersistence,
  saveErrorToast,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { ApprovalLevelForm } from "../ApprovalLevelForm/ApprovalLevelForm";
import { useApprovalLevelMutations } from "../api/useApprovalLevelMutations";
import { extractApprovers } from "../api/useApprovalLevelsList";
import type {
  ApprovalLevelFormValues,
  ApprovalLevelKind,
  ApprovalTrigger,
} from "../types/ApprovalLevel";

const FORM_ID = "approval-level-form";

export const Edit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useV2GetApprovalLevelQuery({ id: Number(id) });
  const { data: usersData } = useV2ListUsersForAccountQuery({});
  const { data: lobsData } = useV2ListLineOfBusinessesForAccountQuery({});
  const { update } = useApprovalLevelMutations();
  const [submitting, setSubmitting] = useState(false);
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ApprovalLevelFormValues>();

  const initial = useMemo<ApprovalLevelFormValues | null>(() => {
    if (!data?.data) return null;
    const record: any = data.data;
    const attrs = record.attributes ?? {};
    return {
      name: attrs.name ?? "",
      kind: (attrs.kind ?? "business") as ApprovalLevelKind,
      signaturesRequired: attrs["signatures-required"] ?? 1,
      submitterMayApprove: !!attrs["submitter-may-approve"],
      limitApprovalsToTeam: !!attrs["limit-approvals-to-team"],
      triggers: (attrs.triggers ?? []) as ApprovalTrigger[],
      approvers: extractApprovers(attrs.approvers),
    };
  }, [data]);

  const availableUsers = useMemo(
    () =>
      (usersData?.data ?? []).map((u: any) => ({
        id: String(u.id),
        name: u.attributes?.name ?? u.attributes?.identifier ?? "",
      })),
    [usersData]
  );

  const availableLobs = useMemo(
    () =>
      (lobsData?.data ?? []).map((l: any) => ({
        id: String(l.id),
        name: l.attributes?.["nested-name"] ?? l.attributes?.name ?? "",
      })),
    [lobsData]
  );

  if (isLoading || !initial) return <ScopeStackSpinner />;

  const formInitial = savedInitial ?? initial;
  const recordName = (data?.data as any)?.attributes?.name || "Approval Level";

  const handleSubmit = async (values: ApprovalLevelFormValues) => {
    setSubmitting(true);
    try {
      await update(id!, values);
      markSaved(values, "Approval level updated.");
    } catch (err) {
      console.error("Failed to update approval level", err);
      setToast(saveErrorToast(err, "approval level"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title={`Edit ${recordName}`}
        formId={FORM_ID}
        onCancel={() => navigate("/admin/approval_levels")}
        isSubmitting={submitting}
      >
        <ApprovalLevelForm
          key={formKey}
          id={FORM_ID}
          initialValues={formInitial}
          availableUsers={availableUsers}
          availableLobs={availableLobs}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
